import React, {
  Fragment,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import DateFnsUtils from "@date-io/date-fns";

// Material-UI Cord
import {
  makeStyles,
  withStyles,
  createMuiTheme,
} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";

// Material-UI Lab
import Autocomplete from "@material-ui/lab/Autocomplete";
import Legend from "../Legend/Legend";

// Material-UI Icon
import LaunchIcon from "@material-ui/icons/Launch";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import ScannerIcon from "@material-ui/icons/Scanner";

// Custom Component
import useLoading from "../Loading/useLoading";
import LockScreenLoading from "../LockScreenLoading/LockScreenLoading";
import { Alert } from "../Alert/Alert";
import { getCompanyList } from "../../dataSource/fileManager";
import WebViewer from "../WebViewer/WebViewer";
// import pdfTronViewer from '@pdftron/webviewer'

// Custom Style
import { uploadFileFormStyle } from "../../styles/jss/components/fileManager/uploadFileFormStyle";

// Fetch Data
import { getFile, getLatestInsertProperty } from "../../dataSource/fileManager";
import ScanFileList from "./ScanFileList";

// Custom Style (Typography)
const InboxTypography = withStyles({
  root: {
    fontSize: 12,
    fontWeight: 400,
    color: "red",
  },
})(Typography);

const crypto = require("crypto");

const useStyles = makeStyles((theme) => uploadFileFormStyle(theme));

const UploadFileForm = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    selectFileOpen,
    acceptedFilesPool,
    setAcceptedFilesPool,
    lockScreenProgressTitle,
    lockScreenLoadingOpen,
    validate,
    validationErrors,
    setValidationErrors,
    scanValidationErrors,
    setFilteredScanFileList,
    filteredScanfileList,
    selection,
    setSelection,
    setScanValidationErrors,
  } = props;

  // Redux Data
  const folderSelectedResult = useSelector(
    (state) => state.folderSelectedRedux
  );
  const uploadFileSourceRedux = useSelector(
    (state) => state.uploadFileSourceRedux
  );
  const uploadFilePropertyRedux = useSelector(
    (state) => state.uploadFilePropertyRedux
  );

  // Lock Screen Loading
  // const [lockScreenProgressTitle, setLockScreenProgressTitle] = useState("")
  // const [lockScreenLoadingOpen, setLockScreenLoadingOpen] = useState(false)

  // Loading
  const [getFileDropLoading, getFileDropShowLoading, getFileDropHiddenLoading] =
    useLoading();

  const [companyListResult, setCompanyListResult] = useState([]);
  const [latestInsertProperty, setLatestInsertProperty] = useState([]);
  const [removeFileRecord, setRemoveFileRecord] = useState([]);

  let fileContent = [];
  let fileProperty = [];

  // const [filesExist, setFilesExist] = useState(null)
  const [webViewerProps, setWebViewerProps] = useState(null);
  const [closePreview, setClosePreview] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    title: "",
    content: "",
  });

  const theme = createMuiTheme();
  theme.typography.h6 = {
    fontSize: "0.4rem",
    fontWeight: "bold",
    "@media (min-width:600px)": {
      fontSize: "0.7rem",
      fontWeight: "bold",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1rem",
      fontWeight: "bold",
    },
  };

  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    // padding: '10px',
    // margin: '10px',
    // padding: '2px',
    // margin: '5px',
    borderWidth: 2,
    borderRadius: 2,
    // borderColor: '#eeeeee',
    borderColor: "#FFFFFF",
    borderStyle: "dashed",
    // backgroundColor: '#fafafa',
    backgroundColor: "#FFFFFF",
    //color: '#bdbdbd',
    color: "#000000",
    outline: "none",
    transition: "border .24s ease-in-out",
    height:
      window.outerHeight - (window.outerHeight - window.innerHeight + 110),
  };

  const activeStyle = {
    borderColor: "blue",
  };

  const acceptStyle = {
    borderColor: "#00e676",
  };

  const rejectStyle = {
    borderColor: "#ff1744",
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    getFileDropShowLoading();

    await handleFileProperty(acceptedFiles);

    getFileDropHiddenLoading();
  }, []);

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    accept:
      "application/pdf, image/*, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    noClick: true,
    onDrop,
  });

  dispatch({
    type: "UPLOAD_FILE_SOURCE_ADD",
    data: acceptedFiles,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const checkFileExist = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = reject;
      reader.onload = async () => {
        const binaryStr = reader.result;
        const fileBuffer = Buffer.from(new Uint8Array(binaryStr));
        const fileRecord = await getFile(
          crypto.createHash("MD5").update(fileBuffer).digest("hex")
        );

        if (fileRecord.data.length > 0) {
          resolve(true);
        } else {
          resolve(false);
        }
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileProperty = async (file) => {
    fileContent = [...fileContent, ...file];

    for (let i = 0; i < file.length; i++) {
      let res = file[i];

      try {
        fileProperty.push({
          filename: res.name,
          folder_seq: folderSelectedResult.folder_seq,
          file_seq: 0,
          company_seq: 0,
          company_seq_option: null,
          inout: "NA",
          is_confidential: false,
          ref_no: "",
          file_subject: "",
          remark: "",
          fileExist: await checkFileExist(res),
          date_of_document: null,
          scan_file_seq: null,
        });
      } catch (error) {
        console.log(error);
      }
    }

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: fileProperty,
    });

    setAcceptedFilesPool(fileContent);
  };

  const removeUploadFileSource = (event) => {
    const fileIndex = event.currentTarget.getAttribute("data-fileindex");

    let data = [...uploadFilePropertyRedux];
    let validate = [...validationErrors];
    data.splice(fileIndex, 1);
    validate.splice(fileIndex, 1);

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: data,
    });

    setValidationErrors(validate);

    acceptedFilesPool.splice(fileIndex, 1);
    fileProperty.splice(fileIndex, 1);

    setRemoveFileRecord(uploadFileSourceRedux.splice(fileIndex, 1));
    setClosePreview(Math.random());
  };

  const removeScanFileSource = (index, file_seq) => {
    const data = [...filteredScanfileList];
    const validate = [...scanValidationErrors];
    data.splice(index, 1);
    validate.splice(index, 1);
    setFilteredScanFileList(data);
    setScanValidationErrors(validate);
    const select = [...selection];
    let selectIndex = select.findIndex((a) => a === file_seq);
    select.splice(selectIndex, 1);

    setSelection(select);
  };

  const filePreview = (file) => () => {
    setWebViewerProps({
      from: "FILE_OBJECT",
      autoLoading: false,
      file: file,
    });
  };

  const scanfilePreview = (file_seq, filename) => () => {
    setWebViewerProps({
      from: "API",
      autoLoading: false,
      file_cat: "master",
      file_ref: file_seq,
      file_name: filename,
    });
  };

  const pasteScannerLatestProperty = (index) => () => {
    let res = [...filteredScanfileList];

    res[index].inout = latestInsertProperty.inout;
    res[index].is_confidential = latestInsertProperty.is_confidential;
    res[index].file_subject = latestInsertProperty.file_subject;
    res[index].date_of_document = latestInsertProperty.date_of_document;
    res[index].ref_no = latestInsertProperty.ref_no;
    res[index].company_seq = latestInsertProperty.company_seq;
    res[index].company_seq_option = companyListResult.find(
      (element) => element.company_seq === latestInsertProperty.company_seq
    );
    res[index].remark = latestInsertProperty.remark;
    setFilteredScanFileList(res);
  };

  const pasteLatestProperty = (index) => () => {
    let res = [...uploadFilePropertyRedux];

    res[index].inout = latestInsertProperty.inout;
    res[index].is_confidential = latestInsertProperty.is_confidential;
    res[index].file_subject = latestInsertProperty.file_subject;
    res[index].date_of_document = latestInsertProperty.date_of_document;
    res[index].ref_no = latestInsertProperty.ref_no;
    res[index].company_seq = latestInsertProperty.company_seq;
    res[index].company_seq_option = companyListResult.find(
      (element) => element.company_seq === latestInsertProperty.company_seq
    );
    res[index].remark = latestInsertProperty.remark;

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: res,
    });
  };

  const handleTextChange = (index) => (event) => {
    const { name, value } = event.target;
    const res = [...uploadFilePropertyRedux];
    res[index][name] = value;

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: res,
    });

    if (
      validationErrors[name] && validationErrors[name] !== "" ? true : false
    ) {
      validate(res);
    }
  };

  const handleSelectChange = (index) => (event) => {
    const { name, value } = event.target;
    const res = [...uploadFilePropertyRedux];

    res[index][name] = value;

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: res,
    });

    if (
      validationErrors[name] && validationErrors[name] !== "" ? true : false
    ) {
      validate(res);
    }
  };

  const handleScanChange = (index, name) => (event) => {
    const data = [...filteredScanfileList];
    data[index][name] = event.target.value;
    setFilteredScanFileList([...data]);
  };

  const handleScanDateChange = (index, name) => (date, value) => {
    const data = [...filteredScanfileList];
    data[index][name] = date;
    setFilteredScanFileList([...data]);
  };

  const handleDateChange = (index, key) => (date, value) => {
    const res = [...uploadFilePropertyRedux];
    res[index][key] = date;

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: res,
    });
  };

  const handleCompanySelectChange = (event, index, value) => {
    const res = [...uploadFilePropertyRedux];

    res[index].company_seq = value ? value.company_seq : 0;
    res[index].company_seq_option = value;

    dispatch({
      type: "UPLOAD_FILE_PROPERTY_ADD",
      data: res,
    });

    if (
      validationErrors.company_seq && validationErrors.company_seq !== ""
        ? true
        : false
    ) {
      validate(res, filteredScanfileList);
    }
  };

  const handleScanCompanySelectChange = (event, index, value) => {
    const data = [...filteredScanfileList];

    data[index].company_seq = value ? value.company_seq : 0;
    data[index].company_seq_option = value;
    setFilteredScanFileList([...data]);

    if (
      scanValidationErrors.company_seq &&
      scanValidationErrors.company_seq !== ""
        ? true
        : false
    ) {
      validate(uploadFilePropertyRedux, data);
    }
  };
  const scanFiles = filteredScanfileList.map((file, index) => {
    return (
      <Paper className={classes.fileDataPaper} key={index}>
        <Chip
          icon={<ScannerIcon style={{ color: "white" }} />}
          label="File From Scanner"
          style={{ backgroundColor: "#2e7d32", color: "white" }}
        />
        <Grid container spacing={1} className={classes.content}>
          <Grid item xs={12} sm={12} lg={8}>
            <TextField
              id={file.filename}
              label="File Name"
              required
              fullWidth={true}
              size="small"
              variant="outlined"
              value={file.filename}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={2}>
            <TextField
              select
              id="inout"
              name="inout"
              label="IN/OUT"
              required
              fullWidth={true}
              size="small"
              variant="outlined"
              value={file.inout || "NA"}
              onChange={handleScanChange(index, "inout")}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem key={"NA"} value={"NA"}>
                <em>NA</em>
              </MenuItem>
              <MenuItem key={"IN"} value={"IN"}>
                IN
              </MenuItem>
              <MenuItem key={"OUT"} value={"OUT"}>
                OUT
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} lg={2}>
            <TextField
              select
              id="is_confidential"
              name="is_confidential"
              label="Confidential"
              required
              fullWidth={true}
              size="small"
              variant="outlined"
              value={file.is_confidential || false}
              onChange={handleScanChange(index, "is_confidential")}
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem key={false} value={false}>
                NO
              </MenuItem>
              <MenuItem key={true} value={true}>
                YES
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.content}>
          <Grid item xs={12} sm={12} lg={9}>
            <TextField
              id={`file_subject[${index}]`}
              name="file_subject"
              label="Subject"
              // required
              fullWidth={true}
              size="small"
              variant="outlined"
              value={file.file_subject || ""}
              onChange={handleScanChange(index, "file_subject")}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} lg={3}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                id="date_of_document"
                name="date_of_document"
                label="Date of Document"
                format="dd/MM/yyyy"
                inputVariant="outlined"
                placeholder="DD/MM/YYYY"
                value={file.date_of_document || null}
                size="small"
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
                onChange={handleScanDateChange(index, "date_of_document")}
                className={classes.datePickerField}
              />
            </MuiPickersUtilsProvider>
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.content}>
          <Grid item xs={12} sm={12} lg={6}>
            <TextField
              id={`ref_no[${index}]`}
              name="ref_no"
              label="Ref. No."
              fullWidth={true}
              size="small"
              variant="outlined"
              value={file.ref_no || ""}
              onChange={handleScanChange(index, "ref_no")}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} lg={6}>
            <Autocomplete
              // multiple
              id="company_seq"
              name="company_seq"
              options={companyListResult}
              getOptionLabel={(option) => option.company_name}
              value={file.company_seq_option || null}
              onChange={(event, value) => {
                handleScanCompanySelectChange(event, index, value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  variant="outlined"
                  label="Organization"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={
                    scanValidationErrors &&
                    scanValidationErrors[index] &&
                    scanValidationErrors.length > 0 &&
                    scanValidationErrors[index]?.company_seq &&
                    scanValidationErrors[index]?.company_seq !== ""
                      ? true
                      : false
                  }
                  helperText={
                    scanValidationErrors.length > 0
                      ? scanValidationErrors[index]?.company_seq
                      : ""
                  }
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.content}>
          <Grid item xs={12} sm={12} lg={12}>
            <TextField
              id={`remark[${index}]`}
              name="remark"
              label="Remark"
              fullWidth={true}
              size="small"
              variant="outlined"
              value={file.remark || ""}
              onChange={handleScanChange(index, "remark")}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} lg={12}>
              <Tooltip title="File Preview">
                <IconButton
                  aria-label="File Preview"
                  color="primary"
                  data-fileindex={index}
                  onClick={scanfilePreview(file.file_seq, file.filename)}
                >
                  <LaunchIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Remove File">
                <IconButton
                  aria-label="Remove File"
                  color="primary"
                  onClick={() => {
                    removeScanFileSource(index, file.file_seq);
                  }}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Paste Latest information filing by own">
                <IconButton
                  aria-label="Paste Latest information"
                  color="primary"
                  onClick={pasteScannerLatestProperty(index)}
                >
                  <AssignmentOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  });

  const files = acceptedFilesPool.map((file, index) => {
    return (
      <Paper className={classes.fileDataPaper} key={index}>
        <Grid container className={classes.contentGroup} key={file.name}>
          <Grid container spacing={1} className={classes.content}>
            <Grid item xs={12} sm={12} lg={8}>
              <TextField
                id={file.name}
                label="File Name"
                required
                fullWidth={true}
                size="small"
                variant="outlined"
                value={file.name}
                InputProps={{
                  readOnly: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} lg={2}>
              <TextField
                select
                id="inout"
                name="inout"
                label="IN/OUT"
                required
                fullWidth={true}
                size="small"
                variant="outlined"
                value={
                  uploadFilePropertyRedux
                    ? uploadFilePropertyRedux.length > 0
                      ? uploadFilePropertyRedux[index].inout
                      : "NA"
                    : ""
                }
                onChange={handleSelectChange(index)}
                error={
                  validationErrors.length > 0 &&
                  validationErrors[index].inout &&
                  validationErrors[index].inout !== ""
                    ? true
                    : false
                }
                helperText={
                  validationErrors.length > 0
                    ? validationErrors[index].inout
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <MenuItem key={"NA"} value={"NA"}>
                  <em>NA</em>
                </MenuItem>
                <MenuItem key={"IN"} value={"IN"}>
                  IN
                </MenuItem>
                <MenuItem key={"OUT"} value={"OUT"}>
                  OUT
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={12} lg={2}>
              <TextField
                select
                id="is_confidential"
                name="is_confidential"
                label="Confidential"
                required
                fullWidth={true}
                size="small"
                variant="outlined"
                value={
                  uploadFilePropertyRedux
                    ? uploadFilePropertyRedux.length > 0
                      ? uploadFilePropertyRedux[index].is_confidential
                      : false
                    : ""
                }
                onChange={handleSelectChange(index)}
                InputLabelProps={{
                  shrink: true,
                }}
              >
                <MenuItem key={false} value={false}>
                  NO
                </MenuItem>
                <MenuItem key={true} value={true}>
                  YES
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Grid container spacing={1} className={classes.content}>
            <Grid item xs={12} sm={12} lg={9}>
              <TextField
                id={`file_subject[${index}]`}
                name="file_subject"
                label="Subject"
                // required
                fullWidth={true}
                size="small"
                variant="outlined"
                value={
                  uploadFilePropertyRedux
                    ? uploadFilePropertyRedux.length > 0
                      ? uploadFilePropertyRedux[index].file_subject
                      : ""
                    : ""
                }
                onChange={handleTextChange(index)}
                error={
                  validationErrors.length > 0 &&
                  validationErrors[index].file_subject &&
                  validationErrors[index].file_subject !== ""
                    ? true
                    : false
                }
                helperText={
                  validationErrors.length > 0
                    ? validationErrors[index].file_subject
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} lg={3}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  id="date_of_document"
                  name="date_of_document"
                  label="Date of Document"
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  placeholder="DD/MM/YYYY"
                  value={
                    uploadFilePropertyRedux
                      ? uploadFilePropertyRedux.length > 0
                        ? uploadFilePropertyRedux[index].date_of_document
                        : null
                      : null
                  }
                  size="small"
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  onChange={handleDateChange(index, "date_of_document")}
                  className={classes.datePickerField}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>

          <Grid container spacing={1} className={classes.content}>
            <Grid item xs={12} sm={12} lg={6}>
              <TextField
                id={`ref_no[${index}]`}
                name="ref_no"
                label="Ref. No."
                // required
                fullWidth={true}
                size="small"
                variant="outlined"
                value={
                  uploadFilePropertyRedux
                    ? uploadFilePropertyRedux.length > 0
                      ? uploadFilePropertyRedux[index].ref_no
                      : ""
                    : ""
                }
                onChange={handleTextChange(index)}
                error={
                  validationErrors.length > 0 &&
                  validationErrors[index].ref_no &&
                  validationErrors[index].ref_no !== ""
                    ? true
                    : false
                }
                helperText={
                  validationErrors.length > 0
                    ? validationErrors[index].ref_no
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} lg={6}>
              <Autocomplete
                // multiple
                id="company_seq"
                name="company_seq"
                options={companyListResult}
                getOptionLabel={(option) => option.company_name}
                value={
                  uploadFilePropertyRedux
                    ? uploadFilePropertyRedux.length > 0
                      ? uploadFilePropertyRedux[index].company_seq_option
                      : null
                    : ""
                }
                onChange={(event, value) => {
                  handleCompanySelectChange(event, index, value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    variant="outlined"
                    label="Organization"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={
                      validationErrors.length > 0 &&
                      validationErrors[index].company_seq &&
                      validationErrors[index].company_seq !== ""
                        ? true
                        : false
                    }
                    helperText={
                      validationErrors.length > 0
                        ? validationErrors[index].company_seq
                        : ""
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1} className={classes.content}>
            <Grid item xs={12} sm={12} lg={12}>
              <TextField
                id={`remark[${index}]`}
                name="remark"
                label="Remark"
                fullWidth={true}
                size="small"
                variant="outlined"
                value={
                  uploadFilePropertyRedux
                    ? uploadFilePropertyRedux.length > 0
                      ? uploadFilePropertyRedux[index].remark
                      : ""
                    : ""
                }
                onChange={handleTextChange(index)}
                error={
                  validationErrors.length > 0 &&
                  validationErrors[index].remark &&
                  validationErrors[index].remark !== ""
                    ? true
                    : false
                }
                helperText={
                  validationErrors.length > 0
                    ? validationErrors[index].remark
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          <InboxTypography>
            {uploadFilePropertyRedux
              ? uploadFilePropertyRedux.length > 0
                ? uploadFilePropertyRedux[index].fileExist
                  ? "File already existing, are you sure to upload again?"
                  : ""
                : ""
              : ""}
          </InboxTypography>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} lg={12}>
              <Tooltip title="File Preview">
                <IconButton
                  aria-label="File Preview"
                  color="primary"
                  data-fileindex={index}
                  onClick={filePreview(file)}
                >
                  <LaunchIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Remove File">
                <IconButton
                  aria-label="Remove File"
                  color="primary"
                  data-fileindex={index}
                  onClick={removeUploadFileSource}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Paste Latest information filing by own">
                <IconButton
                  aria-label="Paste Latest information"
                  color="primary"
                  onClick={pasteLatestProperty(index)}
                >
                  <AssignmentOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    );
  });

  useEffect(() => {
    selectFileOpen(() => open, []);

    getCompanyList(folderSelectedResult.folder_seq)
      .then((res) => {
        setCompanyListResult(res);
      })
      .catch((error) => {
        console.error("Get company list error:");
        console.error(error);
      });

    getLatestInsertProperty(folderSelectedResult.folder_seq)
      .then((res) => {
        setLatestInsertProperty(res.data[0]);
      })
      .catch((error) => {
        console.error("Get Latest Insert Property error:");
        console.error(error);
      });
  }, []);

  return (
    console.log(
      "Laster Insert Property | Variable: latestInsertProperty (UploadFileForm.js)"
    ),
    console.log(latestInsertProperty),
    // console.log("fileSourceRedux | Variable: fileSourceRedux (UploadFileForm.js)"),
    // console.log(fileSourceRedux),

    // console.log("acceptedFilesPool | Variable: acceptedFilesPool (UploadFileForm.js)"),
    // console.log(acceptedFilesPool),

    // console.log("Company List Result | Variable: companyListResult (UploadFileForm.js)"),
    // console.log(companyListResult),

    // console.log("File Upload Fields | Variable: fileUploadFields (UploadFileForm.js)"),
    // console.log(fileUploadFields),

    // console.log("File Upload Form Value | Variable: fileUploadValues (UploadFileForm.js)"),
    // console.log(fileUploadValues),

    // console.log("File Paper List Seq | Variable: filePaperSeqList (UploadFileForm.js)"),
    // console.log(filePaperSeqList),

    // console.log("Web Viewer Props | Variable: webViewerProps (UploadFileForm.js)"),
    // console.log(webViewerProps),

    // console.log("File Upload Form Source (Redux) | Variable: uploadFileSourceRedux (UploadFileForm.js)"),
    // console.log(uploadFileSourceRedux),

    console.log(
      "File Upload Value (Redux) | Variable: uploadFilePropertyRedux (UploadFileForm.js)"
    ),
    (console.log(acceptedFilesPool),
    (
      // console.log("---------------------------------------------------------------------------------"),

      <Fragment>
        <div>
          <LockScreenLoading
            open={lockScreenLoadingOpen}
            circleSize={200}
            progressTitle={lockScreenProgressTitle}
          />

          <Alert
            open={alertOpen}
            alertType={alertData.type}
            alertTitle={alertData.title}
            alertContent={alertData.content}
            handleClose={() => setAlertOpen(false)}
          />
        </div>

        <Grid container>
          <Grid item xs={12} sm={6} lg={6} className={classes.infoArea}>
            {getFileDropLoading}
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              {files}
              {scanFiles}
            </div>
          </Grid>

          <Grid item xs={12} sm={6} lg={6}>
            <div className={classes.viewerArea}>
              <WebViewer
                property={webViewerProps}
                readOnly={true}
                zoom={100}
                move={false}
                ocr={false}
                closeDoc={closePreview}
              />
            </div>
          </Grid>
        </Grid>
      </Fragment>
    ))
  );
};

export default UploadFileForm;
